/**
 * Pure search-filter helpers for the Recipes page.
 *
 * Extracted from RecipesPage.filterRecipes() so it can be unit-tested
 * independently — the original was buried inside a React component and
 * easy to silently break (the "sports alias not matching Icebreaker" bug
 * was exactly this: alias bucket dropped from the filter, no test caught
 * it because there was nothing to call from a test).
 *
 * Search must match against: name, description, tags, match_aliases.
 * All comparisons are diacritic-insensitive (so "ø/o", "é/e", "ä/a"
 * fold together) — mirrors what the backend smart-search does in PASS 1.
 */

/** Lowercase + strip non-Nordic diacritics, while PRESERVING æ/ø/å.
 *
 * Mirrors backend `utils/text_match.normalize_phrase()`. Danish letters
 * are real letters, not diacritics — stripping them would mangle native
 * recipe names (Æbleflæsk → Ableflask, which nobody types or expects).
 * Loan-word diacritics on é/ñ/ü/ö/ç ARE stripped because that's where
 * users disagree (Frosé vs Frose vs frosé).
 */
const _PRESERVE_NORDIC = new Set(['æ', 'ø', 'å', 'Æ', 'Ø', 'Å']);

export const normalizeForSearch = (value) => {
  if (value === null || value === undefined) return '';
  const str = value.toString().toLowerCase();
  let out = '';
  for (const ch of str) {
    if (_PRESERVE_NORDIC.has(ch)) {
      out += ch;
      continue;
    }
    // NFD decompose, drop combining marks.
    out += ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return out;
};

/**
 * Returns true if any of the recipe's searchable fields contains the
 * query (diacritic-insensitive substring match).
 *
 * Searchable fields, in order: name, description, tags[], match_aliases[].
 * An empty/whitespace query matches everything (acts as a no-op).
 */
export const matchesSearchTerm = (recipe, rawQuery) => {
  const q = normalizeForSearch(rawQuery).trim();
  if (!q) return true;
  if (!recipe) return false;

  const haystacks = [
    recipe.name,
    recipe.description,
    ...(recipe.tags || []),
    ...(recipe.match_aliases || []),
  ];
  return haystacks.some((h) => normalizeForSearch(h).includes(q));
};
